import React, { useMemo, useState } from 'react';

import { useDispatch } from 'react-redux';

import { Checkbox, FormControlLabel } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';

import type { WebhookEventSubscription } from '../typescript-types/webhook-event-subscriptions/webhook-event-subscription';

import { createWebhookEventSubscription } from './actions/create-webhook-event-subscription';
import { deleteWebhookEventSubscription } from './actions/delete-webhook-event-subscription';
import { updateWebhookEventSubscription } from './actions/update-webhook-event-subscription';

import { UiButton } from '../ui/ui-button';
import { UiDialog } from '../ui/ui-dialog';

import { WebhookEventSubscriptionContext } from './context/webhook-event-subscription-context';
import { InovaWebhookEventSubscriptionFormBody } from './inova/inova-webhook-event-subscription-form-body';
import { GenericWebhookEventSubscriptionFormBody } from './te-generic/generic-webhook-event-subscription-form-body';

import { inovaEditDescription, inovaWriteDescription } from './inova/utils/inova-component-utils';
import {
  teGenericDescription,
  teGenericEditDescription,
} from './te-generic/utils/te-generic-utils';
import {
  subscriberTypeDisplayName,
  WEBHOOK_EVENT_SUBSCRIPTION_SUBSCRIBER_TYPE,
  WebhookEventSubscriptionEventTriggerType,
  WebhookEventSubscriptionEventType,
  WebhookEventSubscriptionSubscriberType,
} from './utils/webhook-event-subscription-utils';
import { confirm } from '../widgets/confirmation-dialog';

type InputProps = {
  org: Org;
  subscriberType: WebhookEventSubscriptionSubscriberType;
  existingSubscription: WebhookEventSubscription | null;
  onClose: () => void;
};

const SubscriberTypeDialogs = {
  [WEBHOOK_EVENT_SUBSCRIPTION_SUBSCRIBER_TYPE.INOVA]: {
    component: InovaWebhookEventSubscriptionFormBody,
    baseDescription: inovaWriteDescription,
    editDescription: inovaEditDescription,
    learnMoreLink: 'https://help.teamengine.io/article/93-integrating-with-inova',
    requiredSubscriptionDetailFields: ['companyShortName', 'createRecordType'],
  },
  [WEBHOOK_EVENT_SUBSCRIPTION_SUBSCRIBER_TYPE.TEAM_ENGINE_GENERIC]: {
    component: GenericWebhookEventSubscriptionFormBody,
    baseDescription: teGenericDescription,
    editDescription: teGenericEditDescription,
    learnMoreLink: 'https://help.teamengine.io/article/94-webhooks',
    requiredSubscriptionDetailFields: ['postUrl'],
  },
};

const LearnMoreLink = ({ learnMoreLink }: { learnMoreLink: string }) => (
  <a
    className="typo-link u-color-secondary"
    href={learnMoreLink}
    target="_blank"
    rel="noopener noreferrer"
  >
    here
  </a>
);

export const WebhookEventSubscriptionDialog = (props: InputProps) => {
  const { org, subscriberType, existingSubscription, onClose } = props;
  const dispatch = useDispatch();

  const [eventType, setEventType] = useState<WebhookEventSubscriptionEventType | null>(
    existingSubscription?.eventType || null,
  );
  const [eventTriggerType, setEventTriggerType] =
    useState<WebhookEventSubscriptionEventTriggerType | null>(
      existingSubscription?.eventTriggerType || null,
    );
  const [subscriptionDetail, setSubscriptionDetail] = useState<Record<string, any> | null>(
    existingSubscription?.subscriptionDetail || null,
  );
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleToggleDeleting = () => {
    if (existingSubscription && !deletingId) {
      setDeletingId(existingSubscription.webhookEventSubscriptionId);
    } else {
      setDeletingId(null);
    }
  };

  const handleSubmit = async () => {
    if (!eventType || !eventTriggerType || !subscriptionDetail) {
      return;
    }

    if (!existingSubscription) {
      dispatch(
        createWebhookEventSubscription({
          orgId: org.org_id,
          eventType,
          eventTriggerType,
          subscriberType,
          subscriptionDetail,
        }),
      );
    } else if (deletingId) {
      if (
        await confirm(
          `Are you sure you would like to pause your ${subscriberTypeDisplayName(
            subscriberType,
          )} connection?`,
        )
      ) {
        dispatch(
          deleteWebhookEventSubscription(
            deletingId,
            org.org_id,
            existingSubscription?.subscriberType,
          ),
        );
      } else {
        return;
      }
    } else {
      dispatch(
        updateWebhookEventSubscription(
          existingSubscription.webhookEventSubscriptionId,
          existingSubscription.subscriberType,
          {
            eventTriggerType,
            subscriptionDetail,
          },
          org.org_id,
        ),
      );
    }

    onClose();
  };

  const subscriberConfig = SubscriberTypeDialogs[subscriberType];
  const isEditing = !!existingSubscription;
  const isDeleting = !!deletingId;

  const missingRequiredSubscriptionDetail = subscriberConfig.requiredSubscriptionDetailFields.some(
    (field) => !(subscriptionDetail || {})[field],
  );
  const submitDisabled =
    !deletingId &&
    (!eventType ||
      !eventTriggerType ||
      !subscriptionDetail ||
      missingRequiredSubscriptionDetail ||
      !!errorMessage);

  const defaultWebhookEventSubscriptionState = useMemo(
    () => ({
      setEventType,
      setEventTriggerType,
      eventTriggerType,
      setSubscriptionDetail,
      subscriptionDetail,
    }),
    [eventTriggerType, subscriptionDetail],
  );

  return (
    <UiDialog
      title={`Manage ${subscriberTypeDisplayName(subscriberType)} Connection`}
      onClose={onClose}
    >
      <UiDialog.Content>
        <WebhookEventSubscriptionContext.Provider value={defaultWebhookEventSubscriptionState}>
          {isEditing ? (
            <>
              <p className="typo-subtitle">{subscriberConfig.baseDescription}</p>
              {subscriberConfig.editDescription ? (
                <p className="typo-subtitle">
                  {subscriberConfig.editDescription}{' '}
                  <LearnMoreLink learnMoreLink={subscriberConfig.learnMoreLink} />.
                </p>
              ) : null}
              <div className="card card-body">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isDeleting}
                      onChange={handleToggleDeleting}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body1">
                      Pause {subscriberTypeDisplayName(subscriberType)} Connection
                    </Typography>
                  }
                />
                <subscriberConfig.component
                  org={org}
                  errorMessage={errorMessage}
                  setErrorMessage={setErrorMessage}
                  isEditing={isEditing}
                  isDeleting={isDeleting}
                />
              </div>
            </>
          ) : (
            <>
              <p className="typo-subtitle">{subscriberConfig.baseDescription}</p>
              {subscriberConfig.editDescription ? (
                <p className="typo-subtitle">
                  {subscriberConfig.editDescription}{' '}
                  <LearnMoreLink learnMoreLink={subscriberConfig.learnMoreLink} />.
                </p>
              ) : null}
              <div className="card card-body">
                <subscriberConfig.component
                  org={org}
                  errorMessage={errorMessage}
                  setErrorMessage={setErrorMessage}
                  isEditing={isEditing}
                  isDeleting={isDeleting}
                />
              </div>
            </>
          )}
        </WebhookEventSubscriptionContext.Provider>
      </UiDialog.Content>
      <UiDialog.Actions>
        <UiButton onClick={onClose} variant="dismiss">
          Cancel
        </UiButton>
        <UiButton onClick={handleSubmit} disabled={submitDisabled}>
          Save
        </UiButton>
      </UiDialog.Actions>
    </UiDialog>
  );
};
